import React, { useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import Picture from './Picture';
import '../styles/MediaLightbox.css';

const MediaLightbox = ({
    open,
    mediaItems = [],
    activeIndex = 0,
    onClose,
    onNavigate
}) => {
    const { t } = useLanguage();

    const hasMedia = mediaItems.length > 0;
    const clampedIndex = hasMedia
        ? ((activeIndex % mediaItems.length) + mediaItems.length) %
          mediaItems.length
        : 0;
    const currentMedia = hasMedia ? mediaItems[clampedIndex] : null;

    const videoRef = useRef(null);

    const handleKeyDown = useCallback(
        (event) => {
            if (!open || !hasMedia) {
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                onNavigate?.('next');
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                onNavigate?.('prev');
            }
        },
        [hasMedia, onClose, onNavigate, open]
    );

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown, open]);

    useEffect(() => {
        if (
            !open ||
            !currentMedia ||
            currentMedia.type !== 'video' ||
            !videoRef.current
        ) {
            return undefined;
        }

        const videoEl = videoRef.current;

        const enforceMute = () => {
            videoEl.muted = true;
            videoEl.volume = 0;
        };

        const handleVolumeChange = () => {
            enforceMute();
        };

        enforceMute();
        videoEl.addEventListener('volumechange', handleVolumeChange);

        return () => {
            videoEl.removeEventListener('volumechange', handleVolumeChange);
        };
    }, [open, currentMedia]);

    if (!open || !hasMedia || !currentMedia) {
        return null;
    }

    const handleNavigate = (direction) => {
        onNavigate?.(direction);
    };

    return (
        <div className='media-lightbox'>
            <div
                className='media-lightbox__backdrop'
                onClick={onClose}
                aria-hidden='true'
            />
            <div
                className='media-lightbox__window'
                role='dialog'
                aria-modal='true'
                aria-label={currentMedia.label}
            >
                <button
                    type='button'
                    className='media-lightbox__close'
                    onClick={onClose}
                    aria-label={t('common.lightbox.closeLabel')}
                >
                    ×
                </button>
                <div className='media-lightbox__media'>
                    {currentMedia.type === 'video' ? (
                        <video
                            src={currentMedia.src}
                            controls
                            playsInline
                            preload='metadata'
                            muted
                            ref={videoRef}
                        />
                    ) : (
                        <Picture
                            webpSrc={currentMedia.src}
                            fallbackSrc={currentMedia.fallback || currentMedia.src}
                            alt={currentMedia.label}
                        />
                    )}
                </div>
                <div className='media-lightbox__footer'>
                    <span className='media-lightbox__label'>
                        {currentMedia.label}
                    </span>
                    <span className='media-lightbox__count'>
                        {t('common.lightbox.countLabel', {
                            current: clampedIndex + 1,
                            total: mediaItems.length
                        })}
                    </span>
                </div>
                {mediaItems.length > 1 && (
                    <>
                        <button
                            type='button'
                            className='media-lightbox__nav media-lightbox__nav--prev'
                            onClick={() => handleNavigate('prev')}
                            aria-label={t('common.lightbox.prev')}
                        >
                            ‹
                        </button>
                        <button
                            type='button'
                            className='media-lightbox__nav media-lightbox__nav--next'
                            onClick={() => handleNavigate('next')}
                            aria-label={t('common.lightbox.next')}
                        >
                            ›
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default MediaLightbox;
